/**
 * Web Push subscription helper — talks to /api/push/*.
 * Works on Android Chrome always; on iPhone Safari requires the app to be
 * launched from Home Screen (iOS 16.4+ Add-to-Home-Screen).
 */
import axios from "./axiosInstance";

const API = window.location.origin;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/** Detect environments where Web Push is impossible so we can show helpful message. */
export function pushSupportStatus() {
  if (typeof window === "undefined") return { ok: false, reason: "ssr" };
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    (typeof navigator !== "undefined" && navigator.standalone === true);
  if (!("serviceWorker" in navigator)) return { ok: false, reason: "no-sw" };
  if (!("PushManager" in window)) return { ok: false, reason: "no-push" };
  if (isIOS && !standalone) return { ok: false, reason: "ios-need-install", isIOS: true };
  return { ok: true, isIOS, standalone };
}

/** Register the service worker if not already, returns the registration.
 *  Awaits `navigator.serviceWorker.ready` to guarantee the SW is active — critical
 *  on iOS Safari where pushManager.subscribe() fails silently if the SW hasn't
 *  taken control of the page yet. */
export async function ensureServiceWorker() {
  const existing = await navigator.serviceWorker.getRegistration("/");
  if (!existing) {
    await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  }
  // Wait for the SW to become active (state === 'activated')
  return await navigator.serviceWorker.ready;
}

/** Full opt-in flow: permission → subscribe → POST to backend. Must be called from user gesture. */
export async function subscribePush() {
  const status = pushSupportStatus();
  if (!status.ok) {
    if (status.reason === "ios-need-install") {
      throw new Error("Aby otrzymywać powiadomienia na iPhone: otwórz aplikację, dotknij 'Udostępnij' → 'Do ekranu początkowego', a następnie otwórz aplikację z ikony na ekranie.");
    }
    throw new Error("Ta przeglądarka nie obsługuje powiadomień push.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Odmówiono uprawnień do powiadomień. Zezwól w ustawieniach przeglądarki.");
  }

  const registration = await ensureServiceWorker();
  const { data: keyResp } = await axios.get(`${API}/api/push/vapid-public-key`);
  const publicKey = keyResp.publicKey;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  const raw = subscription.toJSON();
  await axios.post(`${API}/api/push/subscribe`, {
    endpoint: raw.endpoint,
    keys: raw.keys,
    user_agent: navigator.userAgent || "",
  });
  return subscription;
}

export async function unsubscribePush() {
  const registration = await navigator.serviceWorker.getRegistration("/");
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  try {
    await axios.post(`${API}/api/push/unsubscribe`, { endpoint: subscription.endpoint });
  } catch (_) { /* ignore */ }
  await subscription.unsubscribe();
}

/** Returns 'granted' | 'denied' | 'default' | 'unsupported' */
export function currentPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

/** Returns whether user has an active subscription on this device. */
export async function isSubscribed() {
  try {
    const registration = await navigator.serviceWorker.getRegistration("/");
    if (!registration) return false;
    const sub = await registration.pushManager.getSubscription();
    return !!sub;
  } catch (_) {
    return false;
  }
}

/** Sends a test notification. Requires an active subscription. */
export async function sendTestPush() {
  const { data } = await axios.post(`${API}/api/push/test`);
  return data;
}
